import React, { useState, useEffect } from 'react';
import styles from './Clients.module.css';
import BookForClient from './BookForClient';
import OverflowMenu from './OverflowMenu';
import Overlay from './Overlay';

function fmt(n) { return `$${Number(n).toFixed(2)}`; }

const SOURCE_META = {
  square:  { label: 'Square',  bg: '#e6f4ea', fg: '#1e7d40' },
  venmo:   { label: 'Venmo',   bg: '#e8f1fb', fg: '#2074c0' },
  account: { label: 'Account', bg: '#f1ecfb', fg: '#6b3dbf' },
};
function SourceBadge({ source }) {
  if (!source) return null;
  const meta = SOURCE_META[source] || { label: source, bg: '#eee', fg: '#555' };
  return (
    <span style={{
      display: 'inline-block', padding: '2px 8px', borderRadius: 10,
      background: meta.bg, color: meta.fg, fontSize: '0.68rem', fontWeight: 700,
      letterSpacing: '0.04em', textTransform: 'uppercase',
    }}>{meta.label}</span>
  );
}
function fmtDate(d) {
  if (!d) return '—';
  const s = typeof d === 'string' ? d : d.toISOString();
  const dateOnly = s.slice(0, 10);
  return new Date(dateOnly + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function calcAge(birthday) {
  if (!birthday) return null;
  const today = new Date();
  const dob = new Date(birthday + 'T12:00:00');
  let age = today.getFullYear() - dob.getFullYear();
  const m = today.getMonth() - dob.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) age--;
  return age;
}

function formatPhone(raw) {
  const digits = raw.replace(/\D/g, '').slice(0, 10);
  if (digits.length <= 3) return digits.length ? `(${digits}` : '';
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

const EMPTY_ATHLETE = { name: '', first_name: '', last_name: '', birthday: '', position: '', height: '', weight: '', team: '', grad_year: '' };
const LOW_BALANCE_THRESHOLD = 75;

// ─── AthleteFields — defined OUTSIDE parent to prevent focus-loss bug ────────
function AthleteFields({ athletes, onChange }) {
  return (
    <div className={styles.athleteSection}>
      <div className={styles.athleteSectionHeader}>
        <span className={styles.sectionLabel}>Athletes</span>
        <button type="button" className={styles.addAthleteBtn}
          onClick={() => onChange([...athletes, { ...EMPTY_ATHLETE }])}>
          + Add Athlete
        </button>
      </div>
      {athletes.map((ath, i) => (
        <div key={i} className={styles.athleteCard}>
          <div className={styles.athleteCardHeader}>
            <span className={styles.athleteNum}>Athlete {i + 1}</span>
            {athletes.length > 1 && (
              <button type="button" className={styles.removeAthleteBtn}
                onClick={() => onChange(athletes.filter((_, idx) => idx !== i))}>
                ✕ Remove
              </button>
            )}
          </div>
          <div className={styles.fieldRow}>
            <div className={styles.field}>
              <label className={styles.fieldLabel}>First Name</label>
              <input className={styles.fieldInput} placeholder="Athlete's first name"
                value={ath.first_name || ath.name?.split(' ')[0] || ''}
                onChange={e => {
                  const a = athletes.map((x, idx) => idx === i ? {
                    ...x,
                    first_name: e.target.value,
                    name: (e.target.value + ' ' + (x.last_name || '')).trim()
                  } : x);
                  onChange(a);
                }} />
            </div>
            <div className={styles.field}>
              <label className={styles.fieldLabel}>Last Name</label>
              <input className={styles.fieldInput} placeholder="Athlete's last name"
                value={ath.last_name || (ath.name?.includes(' ') ? ath.name.split(' ').slice(1).join(' ') : '') || ''}
                onChange={e => {
                  const a = athletes.map((x, idx) => idx === i ? {
                    ...x,
                    last_name: e.target.value,
                    name: ((x.first_name || x.name?.split(' ')[0] || '') + ' ' + e.target.value).trim()
                  } : x);
                  onChange(a);
                }} />
            </div>
          </div>
          <div className={styles.fieldRow}>
            <div className={styles.field}>
              <label className={styles.fieldLabel}>Position / Sport</label>
              <input className={styles.fieldInput} placeholder="e.g. QB, WR, Forward"
                value={ath.position || ''}
                onChange={e => {
                  const a = athletes.map((x, idx) => idx === i ? { ...x, position: e.target.value } : x);
                  onChange(a);
                }} />
            </div>
            <div className={styles.field} />
          </div>
          <div className={styles.fieldRow}>
            <div className={styles.field}>
              <label className={styles.fieldLabel}>Birthday</label>
              <input type="date" className={styles.fieldInput}
                value={ath.birthday || ''}
                onChange={e => {
                  const a = athletes.map((x, idx) => idx === i ? { ...x, birthday: e.target.value } : x);
                  onChange(a);
                }} />
            </div>
            <div className={styles.field}>
              <label className={styles.fieldLabel}>Age (auto-calculated)</label>
              <input className={`${styles.fieldInput} ${styles.fieldReadonly}`} readOnly
                value={ath.birthday ? `${calcAge(ath.birthday)} years old` : '—'} />
            </div>
          </div>
          <div className={styles.fieldRow}>
            <div className={styles.field}>
              <label className={styles.fieldLabel}>Height</label>
              <input className={styles.fieldInput} placeholder="e.g. 5ft 9in"
                value={ath.height || ''}
                onChange={e => {
                  const a = athletes.map((x, idx) => idx === i ? { ...x, height: e.target.value } : x);
                  onChange(a);
                }} />
            </div>
            <div className={styles.field}>
              <label className={styles.fieldLabel}>Weight (lbs)</label>
              <input className={styles.fieldInput} placeholder="e.g. 145"
                value={ath.weight || ''}
                onChange={e => {
                  const a = athletes.map((x, idx) => idx === i ? { ...x, weight: e.target.value } : x);
                  onChange(a);
                }} />
            </div>
          </div>
          <div className={styles.fieldRow}>
            <div className={styles.field}>
              <label className={styles.fieldLabel}>Team / School</label>
              <input className={styles.fieldInput} placeholder="e.g. Parker Youth Football"
                value={ath.team || ''}
                onChange={e => {
                  const a = athletes.map((x, idx) => idx === i ? { ...x, team: e.target.value } : x);
                  onChange(a);
                }} />
            </div>
            <div className={styles.field}>
              <label className={styles.fieldLabel}>Graduating Class</label>
              <input className={styles.fieldInput} placeholder="e.g. 2030"
                value={ath.grad_year || ''}
                onChange={e => {
                  const a = athletes.map((x, idx) => idx === i ? { ...x, grad_year: e.target.value } : x);
                  onChange(a);
                }} />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── EmergencyFields — defined OUTSIDE parent ────────────────────────────────
function EmergencyFields({ data, parentName, parentPhone, onChange }) {
  return (
    <div className={styles.emergencySection}>
      <div className={styles.emergencySectionHeader}>
        <span className={styles.sectionLabel}>Emergency Contact</span>
        <label className={styles.sameAsToggle}>
          <input type="checkbox" checked={!!data.emergencySameAsParent}
            onChange={e => {
              const same = e.target.checked;
              onChange({ ...data, emergencySameAsParent: same, emergencyName: same ? parentName : '', emergencyPhone: same ? parentPhone : '' });
            }} />
          <span>Same as parent</span>
        </label>
      </div>
      <div className={styles.fieldRow}>
        <div className={styles.field}>
          <label className={styles.fieldLabel}>Emergency Contact Name</label>
          <input className={styles.fieldInput} placeholder="Full name"
            value={data.emergencyName || ''} disabled={!!data.emergencySameAsParent}
            onChange={e => onChange({ ...data, emergencyName: e.target.value })} />
        </div>
        <div className={styles.field}>
          <label className={styles.fieldLabel}>Emergency Contact Phone</label>
          <input className={styles.fieldInput} placeholder="(720) 555-0000"
            value={data.emergencyPhone || ''} disabled={!!data.emergencySameAsParent}
            onChange={e => onChange({ ...data, emergencyPhone: formatPhone(e.target.value) })} />
        </div>
      </div>
    </div>
  );
}

// ─── Main component ──────────────────────────────────────────────────────────
export default function Clients({ clients, transactions, config, refresh, showToast, onNavigate, API, initialClientId, authHeader }) {
  const [selectedId, setSelectedId] = useState(initialClientId || null);
  // Auto-collapse list if arriving via sidebar navigation
  const [showAdd, setShowAdd] = useState(false);
  const [showLoad, setShowLoad] = useState(false);
  const [listCollapsed, setListCollapsed] = useState(!!initialClientId);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeSection, setActiveSection] = useState('athlete');
  const [loadConfirmed, setLoadConfirmed] = useState(false);
  const [loadSendEmail, setLoadSendEmail] = useState(true);
  const [loadTarget, setLoadTarget] = useState(null);
  const [customAmount, setCustomAmount] = useState('');
  const [selectedPkg, setSelectedPkg] = useState('');
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState(null);
  const [emailSent, setEmailSent] = useState({});
  const [emailSending, setEmailSending] = useState({});
  const [statementSent, setStatementSent] = useState({});
  const [statementSending, setStatementSending] = useState({});
  const [showStatementPreview, setShowStatementPreview] = useState(false);
  const [statementTarget, setStatementTarget] = useState(null);
  const [confirmDeleteClient, setConfirmDeleteClient] = useState(false);
  const [showBookModal, setShowBookModal] = useState(false);
  const [inactiveClients, setInactiveClients] = useState([]);
  const [showInactive, setShowInactive] = useState(false);
  const [inactiveAthletes, setInactiveAthletes] = useState([]);
  const [showInactiveAthletes, setShowInactiveAthletes] = useState(false);
  const [athleteLoading, setAthleteLoading] = useState(false);
  const [editingAthleteId, setEditingAthleteId] = useState(null);
  const [editAthleteData, setEditAthleteData] = useState({});
  const [addingAthlete, setAddingAthlete] = useState(false);
  const [assessments, setAssessments] = useState([]);
  const [assessmentsLoading, setAssessmentsLoading] = useState(false);
  const [showAssessments, setShowAssessments] = useState({});
  const [addingAssessment, setAddingAssessment] = useState(null);
  const [editingAssessment, setEditingAssessment] = useState(null);
  const [assessmentMetrics, setAssessmentMetrics] = useState([]);
  const BLANK_ASSESSMENT = {
    assessment_date: new Date().toISOString().slice(0, 10),
    assessment_type: 'evaluation',
    strengths: [''],
    weaknesses: [''],
    summary: '',
    scores: []
  };
  const [assessmentForm, setAssessmentForm] = useState({ ...BLANK_ASSESSMENT });
  const [confirmDeleteAssessment, setConfirmDeleteAssessment] = useState(null);
  const [assessmentEmailSent, setAssessmentEmailSent] = useState({});
  const [assessmentEmailSending, setAssessmentEmailSending] = useState({});
  const [editingTx, setEditingTx] = useState(null);
  const [editTxData, setEditTxData] = useState({});
  const [txLoading, setTxLoading] = useState(false);
  const [confirmDeleteTx, setConfirmDeleteTx] = useState(null);
  const [inviteSending, setInviteSending] = useState({});
  const [inviteSent, setInviteSent] = useState({});
  const [newClient, setNewClient] = useState({
    name: '', email: '', phone: '',
    athletes: [{ ...EMPTY_ATHLETE }],
    emergencyName: '', emergencyPhone: '', emergencySameAsParent: false
  });

  // Sync when parent passes a new initialClientId (e.g. dashboard click)
  useEffect(() => {
    if (initialClientId) {
      setSelectedId(initialClientId);
      setEditing(false);
    }
  }, [initialClientId]);

  const selected = clients.find(c => c.id === selectedId);
  const clientTx = transactions
    .filter(t => t.clientId === selectedId)
    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

  const openLoad = (client) => { setLoadTarget(client); setSelectedPkg(''); setCustomAmount(''); setLoadConfirmed(false); setLoadSendEmail(true); setShowLoad(true); };

  const startEdit = (client) => {
    setEditData({
      name: client.name || '', email: client.email || '', phone: client.phone || '',
      athletes: client.athletes?.length
        ? client.athletes.map(a => ({ ...a }))
        : [{ name: client.athlete || '', birthday: client.birthday || '', position: client.position || '' }],
      emergencyName: client.emergencyName || '',
      emergencyPhone: client.emergencyPhone || '',
      emergencySameAsParent: client.emergencySameAsParent || false,
    });
    setEditing(true);
  };

  const handleSaveEdit = async () => {
    setLoading(true);
    try {
      const primaryAthlete = editData.athletes?.[0] || {};
      const payload = { ...editData, athlete: primaryAthlete.name || '', birthday: primaryAthlete.birthday || '', athleteAge: calcAge(primaryAthlete.birthday) };
      const res = await fetch(`${API}/clients/${selected.id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json', ...authHeader() }, body: JSON.stringify(payload) });
      if (!res.ok) { showToast('Save failed', 'error'); return; }
      showToast('Client updated successfully');
      setEditing(false);
      refresh();
    } catch { showToast('Network error', 'error'); }
    finally { setLoading(false); }
  };

  const handleLoadCredit = async () => {
    setLoading(true);
    try {
      const body = customAmount ? { customAmount: parseFloat(customAmount) } : { packageId: selectedPkg };
      const res = await fetch(`${API}/clients/${loadTarget.id}/load-credit`, { method: 'POST', headers: { 'Content-Type': 'application/json', ...authHeader() }, body: JSON.stringify({ ...body, suppressEmail: !loadSendEmail }) });
      if (!res.ok) { const e = await res.json(); showToast(e.error, 'error'); return; }
      const data = await res.json();
      const emailNote = data.emailSent ? ' — receipt emailed to parent' : '';
      showToast(`${fmt(data.transaction.amount)} loaded to ${loadTarget.name}'s account${emailNote}`);
      setShowLoad(false);
      refresh();
    } catch { showToast('Network error', 'error'); }
    finally { setLoading(false); }
  };

  const handleAddClient = async () => {
    if (!newClient.name || !newClient.email) { showToast('Name and email required', 'error'); return; }
    setLoading(true);
    try {
      const primaryAthlete = newClient.athletes?.[0] || {};
      const payload = { ...newClient, athlete: primaryAthlete.name || '', birthday: primaryAthlete.birthday || '', athleteAge: calcAge(primaryAthlete.birthday) };
      const res = await fetch(`${API}/clients`, { method: 'POST', headers: { 'Content-Type': 'application/json', ...authHeader() }, body: JSON.stringify(payload) });
      if (!res.ok) { const e = await res.json(); showToast(e.error, 'error'); return; }
      showToast(`${newClient.name} added successfully`);
      setShowAdd(false);
      setNewClient({ name: '', email: '', phone: '', athletes: [{ ...EMPTY_ATHLETE }], emergencyName: '', emergencyPhone: '', emergencySameAsParent: false });
      refresh();
    } catch { showToast('Network error', 'error'); }
    finally { setLoading(false); }
  };

  // ── Low balance email — now calls real backend endpoint ───────────────────
  const handleSendLowBalanceEmail = async (client) => {
    setEmailSending(prev => ({ ...prev, [client.id]: true }));
    try {
      const res = await fetch(`${API}/clients/${client.id}/send-low-balance-email`, { method: 'POST', headers: { ...authHeader() } });
      const data = await res.json();
      if (data.success) {
        setEmailSent(prev => ({ ...prev, [client.id]: true }));
        showToast(`Low balance email sent to ${client.email}`);
        setTimeout(() => setEmailSent(prev => ({ ...prev, [client.id]: false })), 6000);
      } else {
        showToast(`Email failed: ${data.error}`, 'error');
      }
    } catch { showToast('Network error', 'error'); }
    finally { setEmailSending(prev => ({ ...prev, [client.id]: false })); }
  };

  const handleSendStatement = async (client) => {
    setStatementSending(prev => ({ ...prev, [client.id]: true }));
    try {
      const res = await fetch(`${API}/clients/${client.id}/send-statement-email`, {
        method: 'POST',
        headers: { ...authHeader() }
      });
      const data = await res.json();
      if (data.success) {
        setStatementSent(prev => ({ ...prev, [client.id]: true }));
        showToast(`Account statement sent to ${client.email}`);
        setTimeout(() => setStatementSent(prev => ({ ...prev, [client.id]: false })), 6000);
      } else {
        showToast(`Failed to send statement: ${data.error}`, 'error');
      }
    } catch { showToast('Network error', 'error'); }
    finally { setStatementSending(prev => ({ ...prev, [client.id]: false })); }
  };

  const handleInviteParent = async (client) => {
    if (!window.confirm(
      `Email a setup/reset link to ${client.email}?\n\n` +
      `If they already have an account, this will deactivate their current password and force them to set a new one.`
    )) return;
    setInviteSending(p => ({ ...p, [client.id]: true }));
    try {
      const res = await fetch(`${API}/admin/invite-parent/${client.id}`, { method: 'POST', headers: { ...authHeader() } });
      const data = await res.json();
      if (res.ok && data.success) {
        setInviteSent(p => ({ ...p, [client.id]: true }));
        const verb = data.isReset ? 'Password reset link sent' : 'Invite sent';
        showToast(`${verb} to ${client.email}`);
        setTimeout(() => setInviteSent(p => ({ ...p, [client.id]: false })), 8000);
      } else {
        showToast(data.error || 'Invite failed', 'error');
      }
    } catch { showToast('Network error', 'error'); }
    finally { setInviteSending(p => ({ ...p, [client.id]: false })); }
  };

  const handleDeleteClient = async () => {
    try {
      const res = await fetch(`${API}/clients/${selected.id}`, {
        method: 'DELETE', headers: { ...authHeader() }
      });
      if (!res.ok) { showToast('Delete failed', 'error'); return; }
      showToast(`${selected.name} removed`);
      setConfirmDeleteClient(false);
      setSelectedId(null);
      refresh();
    } catch { showToast('Network error', 'error'); }
  };

  const handleRestoreClient = async (clientId, clientName) => {
    try {
      const res = await fetch(`${API}/clients/${clientId}/restore`, {
        method: 'POST', headers: { ...authHeader() }
      });
      if (!res.ok) { showToast('Restore failed', 'error'); return; }
      showToast(`${clientName} restored`);
      setInactiveClients(prev => prev.filter(c => c.id !== clientId));
      refresh();
    } catch { showToast('Network error', 'error'); }
  };

  const fetchInactiveClients = async () => {
    try {
      const res = await fetch(`${API}/clients/inactive`, { headers: { ...authHeader() } });
      const data = await res.json();
      setInactiveClients(data);
      setShowInactive(true);
    } catch { showToast('Network error', 'error'); }
  };

  const handleDeleteAthlete = async (athleteId, athleteName) => {
    setAthleteLoading(true);
    try {
      const res = await fetch(`${API}/athletes/${athleteId}`, {
        method: 'DELETE', headers: { ...authHeader() }
      });
      if (!res.ok) { showToast('Delete failed', 'error'); return; }
      showToast(`${athleteName} removed`);
      refresh();
    } catch { showToast('Network error', 'error'); }
    finally { setAthleteLoading(false); }
  };

  const handleRestoreAthlete = async (athleteId, athleteName) => {
    setAthleteLoading(true);
    try {
      const res = await fetch(`${API}/athletes/${athleteId}/restore`, {
        method: 'POST', headers: { ...authHeader() }
      });
      if (!res.ok) { showToast('Restore failed', 'error'); return; }
      showToast(`${athleteName} restored`);
      setInactiveAthletes(prev => prev.filter(a => a.id !== athleteId));
      refresh();
    } catch { showToast('Network error', 'error'); }
    finally { setAthleteLoading(false); }
  };

  const fetchInactiveAthletes = async (clientId) => {
    try {
      const res = await fetch(`${API}/clients/${clientId}/athletes/inactive`, { headers: { ...authHeader() } });
      const data = await res.json();
      setInactiveAthletes(data);
      setShowInactiveAthletes(true);
    } catch { showToast('Network error', 'error'); }
  };

  const openEditAthlete = (ath) => {
    // Seed first_name / last_name from the legacy `name` field when missing so
    // the modal inputs are bound directly (no value-fallback that fights deletes).
    const nameParts = (ath.name || '').trim().split(/\s+/);
    const seededFirst = ath.first_name || nameParts[0] || '';
    const seededLast  = ath.last_name  || nameParts.slice(1).join(' ') || '';
    setEditingAthleteId(ath.id);
    setEditAthleteData({ ...ath, first_name: seededFirst, last_name: seededLast });
  };

  const handleSaveAthlete = async () => {
    setAthleteLoading(true);
    try {
      const current = athleteDisplayList(selected);
      const builtName = ((editAthleteData.first_name || '') + ' ' + (editAthleteData.last_name || '')).trim();
      let updatedAthletes;
      if (addingAthlete) {
        if (!builtName) { showToast('Enter at least a first or last name', 'error'); setAthleteLoading(false); return; }
        updatedAthletes = [...current, {
          first_name: editAthleteData.first_name || '',
          last_name: editAthleteData.last_name || '',
          name: builtName,
          position: editAthleteData.position || '',
          birthday: editAthleteData.birthday || '',
          height: editAthleteData.height || '',
          weight: editAthleteData.weight || '',
          team: editAthleteData.team || '',
          grad_year: editAthleteData.grad_year || '',
        }];
      } else {
        updatedAthletes = current.map(a =>
          a.id === editingAthleteId ? {
            ...a,
            first_name: editAthleteData.first_name || '',
            last_name: editAthleteData.last_name || '',
            name: builtName || a.name,
            position: editAthleteData.position || '',
            birthday: editAthleteData.birthday || '',
            height: editAthleteData.height || '',
            weight: editAthleteData.weight || '',
            team: editAthleteData.team || '',
            grad_year: editAthleteData.grad_year || '',
          } : a
        );
      }
      const res = await fetch(`${API}/clients/${selected.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeader() },
        body: JSON.stringify({
          name: selected.name,
          email: selected.email,
          phone: selected.phone,
          athlete: selected.athlete,
          athletes: updatedAthletes,
          emergencyName: selected.emergencyName,
          emergencyPhone: selected.emergencyPhone,
          emergencySameAsParent: selected.emergencySameAsParent,
        })
      });
      if (!res.ok) { showToast('Save failed', 'error'); return; }
      showToast(addingAthlete ? 'Athlete added' : 'Athlete updated');
      setEditingAthleteId(null);
      setAddingAthlete(false);
      refresh();
    } catch { showToast('Network error', 'error'); }
    finally { setAthleteLoading(false); }
  };

  const handleSendAssessmentEmail = async (asmId) => {
    setAssessmentEmailSending(p => ({ ...p, [asmId]: true }));
    try {
      const res = await fetch(`${API}/assessments/${asmId}/send-email`, {
        method: 'POST', headers: { ...authHeader() }
      });
      const data = await res.json();
      if (data.success) {
        setAssessmentEmailSent(p => ({ ...p, [asmId]: true }));
        showToast('Assessment email sent to parent');
        setTimeout(() => setAssessmentEmailSent(p => ({ ...p, [asmId]: false })), 6000);
      } else {
        showToast('Email failed to send', 'error');
      }
    } catch { showToast('Network error', 'error'); }
    finally { setAssessmentEmailSending(p => ({ ...p, [asmId]: false })); }
  };

  // Fetch assessments and metrics when client selected
  const fetchAssessments = async (clientId) => {
    setAssessmentsLoading(true);
    try {
      const [asmRes, metRes] = await Promise.all([
        fetch(`${API}/clients/${clientId}/assessments`, { headers: { ...authHeader() } }),
        fetch(`${API}/config/assessment-metrics`, { headers: { ...authHeader() } }),
      ]);
      const asmData = await asmRes.json();
      const metData = await metRes.json();
      setAssessments(Array.isArray(asmData) ? asmData : []);
      setAssessmentMetrics(Array.isArray(metData) ? metData : []);
    } catch { showToast('Could not load assessments', 'error'); }
    finally { setAssessmentsLoading(false); }
  };

  const openAddAssessment = (athleteId) => {
    const blankScores = assessmentMetrics.map(m => ({
      metric_key: m.key, label: m.label, unit: m.unit, value: '', attempts: []
    }));
    setAssessmentForm({
      assessment_date: new Date().toISOString().slice(0, 10),
      assessment_type: 'evaluation',
      strengths: [''],
      weaknesses: [''],
      summary: '',
      scores: blankScores
    });
    setAddingAssessment(athleteId);
    setEditingAssessment(null);
  };

  const openEditAssessment = (asm) => {
    setAssessmentForm({
      assessment_date: asm.assessment_date?.slice(0, 10) || '',
      assessment_type: asm.assessment_type || 'evaluation',
      strengths: asm.strengths?.length ? asm.strengths : [''],
      weaknesses: asm.weaknesses?.length ? asm.weaknesses : [''],
      summary: asm.summary || '',
      scores: assessmentMetrics.map(m => {
        const existing = asm.scores?.find(s => s.metric_key === m.key);
        return {
          metric_key: m.key,
          label: m.label,
          unit: m.unit,
          value: existing?.value ?? '',
          attempts: existing?.attempts || []
        };
      })
    });
    setEditingAssessment(asm);
    setAddingAssessment(null);
  };

  const handleSaveAssessment = async () => {
    try {
      const body = {
        athlete_id: addingAssessment || editingAssessment.athlete_id,
        client_id: selected.id,
        assessment_date: assessmentForm.assessment_date,
        assessment_type: assessmentForm.assessment_type,
        strengths: assessmentForm.strengths.filter(s => s.trim()),
        weaknesses: assessmentForm.weaknesses.filter(s => s.trim()),
        summary: assessmentForm.summary,
        scores: assessmentForm.scores.filter(s => s.value !== '' && s.value !== null)
      };
      const url = editingAssessment
        ? `${API}/assessments/${editingAssessment.id}`
        : `${API}/assessments`;
      const method = editingAssessment ? 'PUT' : 'POST';
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', ...authHeader() },
        body: JSON.stringify(body)
      });
      if (!res.ok) { const e = await res.json(); showToast(e.error || 'Save failed', 'error'); return; }
      showToast(editingAssessment ? 'Assessment updated' : 'Assessment saved');
      setAddingAssessment(null);
      setEditingAssessment(null);
      fetchAssessments(selected.id);
    } catch { showToast('Network error', 'error'); }
  };

  const handleDeleteAssessment = async (asmId) => {
    try {
      await fetch(`${API}/assessments/${asmId}`, { method: 'DELETE', headers: { ...authHeader() } });
      showToast('Assessment deleted');
      fetchAssessments(selected.id);
    } catch { showToast('Network error', 'error'); }
  };

  const handleGenerateRecruitToken = async (athleteId, athleteName) => {
    try {
      const res = await fetch(`${API}/athletes/${athleteId}/generate-recruit-token`, {
        method: 'POST', headers: { ...authHeader() }
      });
      const data = await res.json();
      const url = `${window.location.origin}/athlete/${data.token}`;
      await navigator.clipboard.writeText(url);
      showToast(`Recruiting link for ${athleteName} copied to clipboard`);
      refresh();
    } catch { showToast('Network error', 'error'); }
  };

  const handleEditTx = (tx) => {
    setEditingTx(tx);
    setEditTxData({
      amount: Math.abs(tx.amount),
      sessionType: tx.sessionType || '',
      sessionDate: tx.sessionDate || tx.createdAt?.slice(0, 10) || new Date().toISOString().slice(0, 10),
      notes: tx.note || '',
      note: tx.note || '',
    });
  };

  const handleSaveTx = async () => {
    setTxLoading(true);
    try {
      const res = await fetch(`${API}/transactions/${editingTx.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeader() },
        body: JSON.stringify({
          amount: parseFloat(editTxData.amount),
          sessionType: editTxData.sessionType,
          sessionDate: editTxData.sessionDate,
          note: editTxData.notes,
        }),
      });
      if (!res.ok) { const e = await res.json(); showToast(e.error || 'Save failed', 'error'); return; }
      showToast('Transaction updated');
      setEditingTx(null);
      refresh();
    } catch { showToast('Network error', 'error'); }
    finally { setTxLoading(false); }
  };

  const handleDeleteTx = async (txId) => {
    setTxLoading(true);
    try {
      const res = await fetch(`${API}/transactions/${txId}`, {
        method: 'DELETE',
        headers: { ...authHeader() },
      });
      if (!res.ok) { const e = await res.json(); showToast(e.error || 'Delete failed', 'error'); return; }
      showToast('Transaction deleted');
      setConfirmDeleteTx(null);
      refresh();
    } catch { showToast('Network error', 'error'); }
    finally { setTxLoading(false); }
  };

  useEffect(() => {
    if (selectedId) {
      fetchAssessments(selectedId);
      setAddingAssessment(null);
      setEditingAssessment(null);
      setActiveSection('athlete');
    }
  }, [selectedId]);

  const familyName = (client) => {
    if (!client) return '';
    const parts = client.name.split(' ');
    return parts.length > 1 ? parts[parts.length - 1] + ' Family' : client.name;
  };

  const balanceStatus = (b) => b <= 0 ? 'empty' : b < LOW_BALANCE_THRESHOLD ? 'low' : b < 150 ? 'ok' : 'good';

  const athleteDisplayList = (client) => {
    if (client.athletes?.filter(a => a.name).length) return client.athletes.filter(a => a.name);
    if (client.athlete) return [{ name: client.athlete, birthday: client.birthday, position: client.position }];
    return [];
  };

  const normalizedQuery = searchQuery.trim().toLowerCase();
  const filteredClients = !normalizedQuery ? clients : clients.filter(c => {
    const haystack = [
      c.name, c.email, c.phone, c.athlete,
      ...(c.athletes || []).map(a => a.name).filter(Boolean),
    ].filter(Boolean).join(' ').toLowerCase();
    return haystack.includes(normalizedQuery);
  });

  return (
    <div className={styles.page}>
      <div className={`${styles.layout} ${listCollapsed ? styles.layoutCollapsed : ''}`}>

        {/* Client list */}
        <div className={styles.list}>
          <div className={styles.listHeader}>
            <span className={styles.listTitle}>
              {normalizedQuery ? `${filteredClients.length} of ${clients.length}` : `${clients.length} Clients`}
            </span>
            <button className={styles.addBtn} onClick={() => setShowAdd(true)}>+ Add Client</button>
          </div>
          <div className={styles.searchRow}>
            <input
              type="text"
              className={styles.searchInput}
              placeholder="Search name, athlete, email, phone…"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button
                type="button"
                className={styles.searchClear}
                onClick={() => setSearchQuery('')}
                aria-label="Clear search"
              >×</button>
            )}
          </div>
          {filteredClients.length === 0 && normalizedQuery && (
            <div className={styles.emptyMatch}>No clients match "{searchQuery}"</div>
          )}
          {filteredClients.map(c => (
            <button key={c.id}
              className={`${styles.clientRow} ${selectedId === c.id ? styles.clientRowActive : ''}`}
              onClick={() => { setSelectedId(c.id); setEditing(false); setListCollapsed(true); }}>
              <div className={styles.avatar}>{c.name.charAt(0)}</div>
              <div className={styles.rowInfo}>
                <div className={styles.rowName}>{c.name}</div>
                <div className={styles.rowAthlete}>{athleteDisplayList(c).map(a => a.name).join(', ') || 'No athlete set'}</div>
              </div>
              <div className={`${styles.rowBal} ${styles[`bal_${balanceStatus(c.balance)}`]}`}>{fmt(c.balance)}</div>
            </button>
          ))}

          {/* Inactive clients section */}
          {!showInactive ? (
            <button className={styles.showInactiveClientBtn} onClick={fetchInactiveClients}>
              Show removed clients
            </button>
          ) : (
            <>
              {inactiveClients.length === 0
                ? <div className={styles.inactiveEmpty}>No removed clients</div>
                : inactiveClients.map(c => (
                  <div key={c.id} className={styles.inactiveClientRow}>
                    <div className={styles.avatar} style={{ opacity: 0.4 }}>{c.name.charAt(0)}</div>
                    <div className={styles.rowInfo}>
                      <div className={styles.rowName} style={{ opacity: 0.5 }}>{c.name}</div>
                      <div className={styles.rowAthlete} style={{ opacity: 0.4 }}>Removed</div>
                    </div>
                    <button className={styles.restoreClientBtn}
                      onClick={() => handleRestoreClient(c.id, c.name)}>Restore</button>
                  </div>
                ))
              }
              <button className={styles.showInactiveClientBtn}
                onClick={() => { setShowInactive(false); setInactiveClients([]); }}>
                Hide removed clients
              </button>
            </>
          )}
        </div>

        {/* Detail panel */}
        <div className={styles.detail}>
          {!selected ? (
            <div className={styles.emptyDetail}>
              <span className={styles.emptyIcon}>◎</span>
              <p>Select a client to view their account</p>
            </div>

          ) : editing ? (
            /* Edit mode */
            <div className={styles.editForm}>
              <div className={styles.editHeader}>
                <h2 className={styles.editTitle}>Edit Client</h2>
                <div className={styles.editActions}>
                  <button className={styles.cancelBtn} onClick={() => setEditing(false)}>Cancel</button>
                  <button className={styles.saveBtn} disabled={loading} onClick={handleSaveEdit}>
                    {loading ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              </div>
              <div className={styles.editSection}>
                <div className={styles.editSectionTitle}>Parent / Guardian</div>
                <div className={styles.fieldRow}>
                  <div className={styles.field}>
                    <label className={styles.fieldLabel}>Parent Name</label>
                    <input className={styles.fieldInput} value={editData.name} onChange={e => setEditData(p => ({ ...p, name: e.target.value }))} />
                  </div>
                  <div className={styles.field}>
                    <label className={styles.fieldLabel}>Email</label>
                    <input className={styles.fieldInput} type="email" value={editData.email} onChange={e => setEditData(p => ({ ...p, email: e.target.value }))} />
                  </div>
                </div>
                <div className={styles.fieldRow}>
                  <div className={styles.field}>
                    <label className={styles.fieldLabel}>Phone</label>
                    <input className={styles.fieldInput} placeholder="(720) 555-0000" value={editData.phone} onChange={e => setEditData(p => ({ ...p, phone: formatPhone(e.target.value) }))} />
                  </div>
                  <div className={styles.field} />
                </div>
              </div>
              <AthleteFields athletes={editData.athletes} onChange={athletes => setEditData(p => ({ ...p, athletes }))} />
              <EmergencyFields data={editData} parentName={editData.name} parentPhone={editData.phone} onChange={updated => setEditData(p => ({ ...p, ...updated }))} />
            </div>

          ) : (
            /* View mode */
            <>
              {/* ── Sticky header ──────────────────────────────────────── */}
              <div className={styles.detailHeader}>
                {listCollapsed && (
                  <button className={styles.expandListBtn} onClick={() => setListCollapsed(false)} title="Show client list">
                    ‹
                  </button>
                )}
                <div className={styles.detailHeaderLeft}>
                  <div className={styles.detailAvatar}>{selected.name.charAt(0)}</div>
                  <div className={styles.detailInfo}>
                    <div className={styles.detailFamilyName}>{familyName(selected)}</div>
                    {athleteDisplayList(selected).length > 0 && (
                      <div className={styles.detailAthleteName}>
                        {athleteDisplayList(selected).map(a => a.name).join(' & ')}
                      </div>
                    )}
                    <div className={styles.detailEmail}>{selected.email}</div>
                  </div>
                </div>
                <div className={styles.detailHeaderBtns}>
                  {!listCollapsed && (
                    <button className={styles.collapseBtn} onClick={() => setListCollapsed(true)} title="Expand to full width">⤢</button>
                  )}
                  <button className={styles.loadBtn} onClick={() => openLoad(selected)}>$ Load Credits</button>
                  <button className={styles.chargeBtn} onClick={() => onNavigate && onNavigate('charge', selected.id)}>⚡ Charge Session</button>
                  <button className={styles.bookBtn} onClick={() => setShowBookModal(true)}>📅 Book Session</button>
                  <OverflowMenu
                    items={[
                      { icon: '✎', label: 'Edit Client', onClick: () => startEdit(selected) },
                      {
                        icon: '✉',
                        label: statementSent[selected.id] ? 'Statement Sent' : 'Send Statement',
                        disabled: statementSent[selected.id] || statementSending[selected.id],
                        onClick: () => { setStatementTarget(selected); setShowStatementPreview(true); },
                      },
                      {
                        icon: '👤',
                        label: inviteSending[selected.id]
                          ? 'Sending…'
                          : inviteSent[selected.id] ? 'Link Sent' : 'Invite / Reset Password',
                        disabled: inviteSent[selected.id] || inviteSending[selected.id],
                        onClick: () => handleInviteParent(selected),
                      },
                      { divider: true },
                      { icon: '✕', label: 'Remove Client', danger: true, onClick: () => setConfirmDeleteClient(true) },
                    ]}
                  />
                </div>
              </div>

              {/* ── Section nav ────────────────────────────────────────── */}
              <div className={styles.sectionNav}>
                {[
                  { key: 'account', label: '💰 Account' },
                  { key: 'athlete', label: '🏃 Athlete' },
                  { key: 'transactions', label: '📋 Transactions' },
                ].map(s => (
                  <button key={s.key}
                    className={`${styles.sectionNavBtn} ${activeSection === s.key ? styles.sectionNavActive : ''}`}
                    onClick={() => setActiveSection(s.key)}>
                    {s.label}
                  </button>
                ))}
              </div>

              {/* ══ ATHLETE SECTION ════════════════════════════════════════ */}
              {activeSection === 'athlete' && (
              <div className={styles.sectionBody}>

              {/* Athletes */}
              <div className={styles.athleteBox}>
                <div className={styles.athleteBoxHeader}>
                  <div className={styles.sectionLabel}>Athletes</div>
                </div>
                {athleteDisplayList(selected).length === 0
                  ? <p className={styles.noData}>No athletes added. Click <strong>✎ Edit</strong> to add.</p>
                  : athleteDisplayList(selected).map((ath, i) => {
                    const athAssessments = assessments.filter(a => a.athlete_id === ath.id);
                    const isExpanded = showAssessments[ath.id];
                    return (
                      <div key={i} className={styles.athleteBlock}>
                        {/* Athlete header card */}
                        <div className={styles.athleteViewCard}>
                          <div className={styles.athleteViewAvatar}>{ath.name?.charAt(0)}</div>
                          <div className={styles.athleteViewInfo}>
                            <div className={styles.athleteViewName}>{ath.name}</div>
                            <div className={styles.athleteViewMeta}>
                              {ath.position && <span className={styles.metaChip}>{ath.position}</span>}
                              {ath.grad_year && <span className={styles.metaChip}>Class of {ath.grad_year}</span>}
                              {ath.team && <span className={styles.metaChip}>{ath.team}</span>}
                              {ath.height && <span className={styles.metaChip}>{ath.height}</span>}
                              {ath.weight && <span className={styles.metaChip}>{ath.weight} lbs</span>}
                              {ath.birthday && <span className={styles.metaChip}>Age {calcAge(ath.birthday)}</span>}
                            </div>
                          </div>
                          <div className={styles.athleteCardActions}>
                            {ath.id && (
                              <button className={styles.recruitLinkBtn}
                                onClick={() => handleGenerateRecruitToken(ath.id, ath.name)}>
                                🔗 Recruit
                              </button>
                            )}
                            {ath.id && (
                              <button className={styles.editAthleteBtn}
                                onClick={() => openEditAthlete(ath)}>✎ Edit</button>
                            )}
                            {ath.id && (
                              <button className={styles.athleteDeleteBtn} disabled={athleteLoading}
                                onClick={() => handleDeleteAthlete(ath.id, ath.name)}>Remove</button>
                            )}
                          </div>
                        </div>

                        {/* Assessments section per athlete */}
                        {ath.id && (
                          <div className={styles.assessmentSection}>
                            <div className={styles.assessmentSectionHeader}>
                              <button className={styles.assessmentToggle}
                                onClick={() => setShowAssessments(p => ({ ...p, [ath.id]: !p[ath.id] }))}>
                                {isExpanded ? '▾' : '▸'} Assessments ({athAssessments.length})
                              </button>
                              <button className={styles.addAssessmentBtn}
                                onClick={() => openAddAssessment(ath.id)}>
                                + Add Assessment
                              </button>
                            </div>

                            {isExpanded && (
                              <div className={styles.assessmentList}>
                                {assessmentsLoading && <p className={styles.noData}>Loading...</p>}
                                {!assessmentsLoading && athAssessments.length === 0 && (
                                  <p className={styles.noData}>No assessments yet. Click + Add Assessment to get started.</p>
                                )}
                                {athAssessments.map(asm => (
                                  <div key={asm.id} className={styles.assessmentCard}>
                                    <div className={styles.assessmentCardHeader}>
                                      <span className={styles.assessmentDate}>{fmtDate(asm.assessment_date)}</span>
                                      <span style={{
                                        fontSize: '0.7rem', padding: '2px 8px', borderRadius: 4,
                                        background: asm.assessment_type === 'test_day' ? '#C04A0E' : '#555',
                                        color: '#fff', marginLeft: 8, fontWeight: 700,
                                        textTransform: 'uppercase', letterSpacing: '0.5px'
                                      }}>
                                        {asm.assessment_type === 'test_day' ? 'Test Day' : 'Evaluation'}
                                      </span>
                                      <div className={styles.assessmentCardActions}>
                                        {(() => {
                                          const hasContent = (asm.scores?.length > 0) || (asm.strengths?.length > 0);
                                          const sent = assessmentEmailSent[asm.id];
                                          const sending = assessmentEmailSending[asm.id];
                                          return (
                                            <button
                                              className={`${styles.sendAssessmentBtn} ${hasContent ? styles.sendAssessmentActive : styles.sendAssessmentInactive} ${sent ? styles.sendAssessmentSent : ''}`}
                                              disabled={!hasContent || sent || sending}
                                              onClick={() => handleSendAssessmentEmail(asm.id)}
                                            >
                                              {sending ? 'Sending...' : sent ? '✓ Sent' : '✉ Send to Parent'}
                                            </button>
                                          );
                                        })()}
                                        <button className={styles.txEditBtn} onClick={() => openEditAssessment(asm)}>✎ Edit</button>
                                        <button className={styles.txDeleteBtn} onClick={() => setConfirmDeleteAssessment(asm.id)}>Delete</button>
                                      </div>
                                    </div>
                                    {asm.scores?.length > 0 && (
                                      <div className={styles.scoreGrid}>
                                        {asm.scores.map(s => (
                                          <div key={s.id} className={styles.scoreChip}>
                                            <div className={styles.scoreLabel}>{s.label}</div>
                                            <div className={styles.scoreValue}>{s.value} <span className={styles.scoreUnit}>{s.unit}</span></div>
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                    {asm.strengths?.length > 0 && (
                                      <div className={styles.asmBullets}>
                                        <div className={styles.asmBulletsLabel}>Strengths</div>
                                        {asm.strengths.map((s, i) => <div key={i} className={styles.strengthBullet}>+ {s}</div>)}
                                      </div>
                                    )}
                                    {asm.weaknesses?.length > 0 && (
                                      <div className={styles.asmBullets}>
                                        <div className={styles.asmBulletsLabel}>Areas to Improve</div>
                                        {asm.weaknesses.map((w, i) => <div key={i} className={styles.weaknessBullet}>→ {w}</div>)}
                                      </div>
                                    )}
                                    {asm.summary && <p className={styles.asmSummary}>{asm.summary}</p>}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })
                }
                {/* Inactive athletes */}
                <div className={styles.athleteActionsRow}>
                  <button
                    type="button"
                    className={`${styles.athleteActionPill} ${styles.addAthletePill}`}
                    onClick={() => { setAddingAthlete(true); setEditAthleteData({}); }}
                  >
                    + Add Athlete
                  </button>
                  {!showInactiveAthletes ? (
                    <button
                      type="button"
                      className={`${styles.athleteActionPill} ${styles.showInactivePill}`}
                      onClick={() => fetchInactiveAthletes(selected.id)}
                    >
                      Show removed athletes
                    </button>
                  ) : (
                    <button
                      type="button"
                      className={`${styles.athleteActionPill} ${styles.showInactivePill}`}
                      onClick={() => { setShowInactiveAthletes(false); setInactiveAthletes([]); }}
                    >
                      Hide removed athletes
                    </button>
                  )}
                </div>
                {showInactiveAthletes && (
                  <>
                    {inactiveAthletes.length === 0
                      ? <p className={styles.noData} style={{ fontSize: '0.78rem', color: 'var(--gray-3)' }}>No removed athletes.</p>
                      : inactiveAthletes.map(ath => (
                        <div key={ath.id} className={`${styles.athleteViewCard} ${styles.athleteInactive}`}>
                          <div className={styles.athleteViewAvatar} style={{ opacity: 0.4 }}>{ath.name?.charAt(0)}</div>
                          <div className={styles.athleteViewInfo}>
                            <div className={styles.athleteViewName} style={{ opacity: 0.5 }}>{ath.name}</div>
                            <div style={{ fontSize: '0.72rem', color: 'var(--gray-3)' }}>Removed</div>
                          </div>
                          <button className={styles.restoreBtn} disabled={athleteLoading}
                            onClick={() => handleRestoreAthlete(ath.id, ath.name)}>Restore</button>
                        </div>
                      ))
                    }
                  </>
                )}
              </div>

              </div>)} {/* end athlete section */}

              {/* ══ ACCOUNT SECTION ════════════════════════════════════════ */}
              {activeSection === 'account' && (
              <div className={styles.sectionBody}>

              {/* Balance — full view in account section */}
              <div className={styles.balanceBox}>
                <div className={styles.balLabel}>Current Account Balance</div>
                <div className={`${styles.balValue} ${styles[`bal_${balanceStatus(selected.balance)}`]}`}>{fmt(selected.balance)}</div>
                {selected.balance < LOW_BALANCE_THRESHOLD && (
                  <div className={styles.balWarning}>
                    <span>{selected.balance <= 0 ? '⚠ Account empty — load credits before next session' : `⚠ Low balance — only ${fmt(selected.balance)} remaining`}</span>
                    <button
                      className={`${styles.emailAlertBtn} ${emailSent[selected.id] ? styles.emailAlertSent : ''}`}
                      onClick={() => handleSendLowBalanceEmail(selected)}
                      disabled={emailSent[selected.id] || emailSending[selected.id]}
                    >
                      {emailSending[selected.id] ? 'Sending...' : emailSent[selected.id] ? '✓ Email Sent' : '✉ Notify Parent'}
                    </button>
                  </div>
                )}
              </div>

              {/* Emergency contact */}
              {(selected.emergencyName || selected.emergencySameAsParent) && (
                <div className={styles.emergencyBox}>
                  <div className={styles.sectionLabel}>Emergency Contact</div>
                  <div className={styles.infoGrid}>
                    <div className={styles.infoItem}>
                      <span className={styles.infoKey}>Name</span>
                      <span className={styles.infoVal}>
                        {selected.emergencySameAsParent ? selected.name : selected.emergencyName}
                        {selected.emergencySameAsParent && <span className={styles.sameTag}>Same as parent</span>}
                      </span>
                    </div>
                    <div className={styles.infoItem}>
                      <span className={styles.infoKey}>Phone</span>
                      <span className={styles.infoVal}>{selected.emergencySameAsParent ? selected.phone : selected.emergencyPhone}</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Stats */}
              <div className={styles.statsBox}>
                <div className={styles.infoGrid}>
                  <div className={styles.infoItem}>
                    <span className={styles.infoKey}>Client Since</span>
                    <span className={styles.infoVal}>{fmtDate(selected.createdAt)}</span>
                  </div>
                  <div className={styles.infoItem}>
                    <span className={styles.infoKey}>Total Sessions</span>
                    <span className={styles.infoVal}>{clientTx.filter(t => t.type === 'session_debit').length}</span>
                  </div>
                </div>
              </div>

              </div>)} {/* end account section */}

              {/* ══ TRANSACTIONS SECTION ═══════════════════════════════════ */}
              {activeSection === 'transactions' && (
              <div className={styles.sectionBody}>
              {/* Transaction history */}
              <div className={styles.txSection}>
                <div className={styles.txHeader}>Transaction History</div>
                {clientTx.length === 0 && <p className={styles.noTx}>No transactions yet.</p>}
                {clientTx.map(tx => (
                  <div key={tx.id} className={styles.txRow}>
                    <div className={styles.txLeft}>
                      <span className={`tag ${tx.type === 'credit_load' ? 'tag-green' : 'tag-orange'}`}>
                        {tx.type === 'credit_load' ? '+ Credit' : tx.sessionType}
                      </span>
                      <div>
                        <div className={styles.txNote}>{tx.note}</div>
                        <div className={styles.txDate}>{fmtDate(tx.sessionDate || tx.createdAt)}</div>
                      </div>
                    </div>
                    <div className={styles.txRight}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'flex-end' }}>
                        <SourceBadge source={tx.source} />
                        <div className={`${styles.txAmount} ${tx.amount > 0 ? styles.pos : styles.neg}`}>
                          {tx.amount > 0 ? '+' : ''}{fmt(tx.amount)}
                        </div>
                      </div>
                      <div className={styles.txBalance}>bal: {fmt(tx.balanceAfter)}</div>
                      <div className={styles.txActions}>
                        <button className={styles.txEditBtn} onClick={() => handleEditTx(tx)} title="Edit">✎</button>
                        <button className={styles.txDeleteBtn} onClick={() => setConfirmDeleteTx(tx.id)} title="Delete">✕</button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              </div>)} {/* end transactions section */}
            </>
          )}
        </div>
      </div>

      {/* Load Credits Modal */}
      {showLoad && loadTarget && config && (
        <Overlay className={styles.overlay} onClose={() => setShowLoad(false)}>
          <div className={styles.modal} onClick={e => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3 className={styles.modalTitle}>Load Credits — {loadTarget.name}</h3>
              <button className={styles.modalClose} onClick={() => setShowLoad(false)}>✕</button>
            </div>
            <div className={styles.modalBody}>
              {!loadConfirmed ? (
                <>
                  <div className={styles.pkgList}>
                    {config.creditPackages.map(pkg => (
                      <button key={pkg.id}
                        className={`${styles.pkgCard} ${selectedPkg === pkg.id ? styles.pkgSelected : ''}`}
                        onClick={() => { setSelectedPkg(pkg.id); setCustomAmount(''); }}>
                        <div className={styles.pkgLabel}>{pkg.label}</div>
                        <div className={styles.pkgAmount}>{fmt(pkg.amount)}</div>
                        <div className={styles.pkgDesc}>{pkg.description}</div>
                      </button>
                    ))}
                  </div>
                  <div className={styles.orDivider}><span>or enter custom amount</span></div>
                  <input className={styles.customInput} type="number" placeholder="Custom amount (e.g. 200)"
                    value={customAmount} onChange={e => { setCustomAmount(e.target.value); setSelectedPkg(''); }} />
                  <button className={styles.loadConfirmBtn}
                    disabled={!selectedPkg && !customAmount}
                    onClick={() => { if (selectedPkg || customAmount) setLoadConfirmed(true); }}>
                    Continue →
                  </button>
                </>
              ) : (
                <>
                  <div className={styles.confirmSummary || ''} style={{ background: 'var(--surface-3)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', overflow: 'hidden', marginBottom: 4 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 14px', borderBottom: '1px solid var(--border)', fontSize: '0.85rem' }}>
                      <span style={{ color: 'var(--gray-2)' }}>Client</span>
                      <span style={{ fontWeight: 600 }}>{loadTarget.name}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 14px', borderBottom: '1px solid var(--border)', fontSize: '0.85rem' }}>
                      <span style={{ color: 'var(--gray-2)' }}>Package</span>
                      <span style={{ fontWeight: 600 }}>
                        {selectedPkg ? config.creditPackages.find(p => p.id === selectedPkg)?.label : `Custom`}
                      </span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 14px', borderBottom: '1px solid var(--border)', fontSize: '0.85rem' }}>
                      <span style={{ color: 'var(--gray-2)' }}>Amount</span>
                      <span style={{ fontWeight: 700, color: 'var(--green)' }}>
                        +{fmt(selectedPkg ? (config.creditPackages.find(p => p.id === selectedPkg)?.amount || 0) : parseFloat(customAmount))}
                      </span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 14px', fontSize: '0.85rem' }}>
                      <span style={{ color: 'var(--gray-2)' }}>New Balance</span>
                      <span style={{ fontWeight: 700, color: 'var(--green)' }}>
                        {fmt((parseFloat(loadTarget.balance) || 0) + (selectedPkg ? (config.creditPackages.find(p => p.id === selectedPkg)?.amount || 0) : parseFloat(customAmount) || 0))}
                      </span>
                    </div>
                  </div>
                  <label className={styles.emailToggle} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, background: 'var(--surface-3)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '12px 16px', cursor: 'pointer', fontSize: '0.82rem', color: 'var(--gray-2)' }}>
                    <input type="checkbox" checked={loadSendEmail} onChange={e => setLoadSendEmail(e.target.checked)}
                      style={{ width: 16, height: 16, accentColor: 'var(--orange)', flexShrink: 0, marginTop: 2 }} />
                    <span>{loadSendEmail ? <>✉ Receipt will be emailed to {loadTarget.email}</> : <>📭 No receipt email will be sent</>}</span>
                  </label>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button className={styles.loadConfirmBtn}
                      style={{ background: 'var(--surface-3)', color: 'var(--gray-1)', border: '1px solid var(--border)', flex: 1 }}
                      onClick={() => setLoadConfirmed(false)}>
                      ← Back
                    </button>
                    <button className={styles.loadConfirmBtn} style={{ flex: 2 }}
                      disabled={loading} onClick={handleLoadCredit}>
                      {loading ? 'Processing...' : loadSendEmail
                        ? `Load ${fmt(selectedPkg ? (config.creditPackages.find(p => p.id === selectedPkg)?.amount || 0) : parseFloat(customAmount))} & Send Receipt`
                        : `Load ${fmt(selectedPkg ? (config.creditPackages.find(p => p.id === selectedPkg)?.amount || 0) : parseFloat(customAmount))}`}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </Overlay>
      )}

      {/* Edit Transaction Modal */}
      {editingTx && (
        <Overlay className={styles.overlay} onClose={() => setEditingTx(null)}>
          <div className={styles.modal} onClick={e => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3 className={styles.modalTitle}>Edit Transaction</h3>
              <button className={styles.modalClose} onClick={() => setEditingTx(null)}>✕</button>
            </div>
            <div className={styles.modalBody}>
              <div className={styles.formField}>
                <label className={styles.formLabel}>Amount ($)</label>
                <input
                  className={styles.formInput}
                  type="number"
                  min="0"
                  step="0.01"
                  value={editTxData.amount}
                  onChange={e => setEditTxData(p => ({ ...p, amount: e.target.value }))}
                />
              </div>
              {editingTx.type === 'session_debit' && (
                <>
                  <div className={styles.formField}>
                    <label className={styles.formLabel}>Session Date</label>
                    <input
                      type="date"
                      className={styles.formInput}
                      value={editTxData.sessionDate}
                      onChange={e => setEditTxData(p => ({ ...p, sessionDate: e.target.value }))}
                    />
                  </div>
                  <div className={styles.formField}>
                    <label className={styles.formLabel}>Notes</label>
                    <input
                      className={styles.formInput}
                      value={editTxData.notes}
                      onChange={e => setEditTxData(p => ({ ...p, notes: e.target.value }))}
                      placeholder="Optional notes"
                    />
                  </div>
                </>
              )}
              {editingTx.type === 'credit_load' && (
                <>
                  <div className={styles.formField}>
                    <label className={styles.formLabel}>Date</label>
                    <input
                      type="date"
                      className={styles.formInput}
                      value={editTxData.sessionDate}
                      onChange={e => setEditTxData(p => ({ ...p, sessionDate: e.target.value }))}
                    />
                  </div>
                  <div className={styles.formField}>
                    <label className={styles.formLabel}>Note / Label</label>
                    <input
                      className={styles.formInput}
                      value={editTxData.notes}
                      onChange={e => setEditTxData(p => ({ ...p, notes: e.target.value }))}
                    />
                  </div>
                </>
              )}
              <p style={{ fontSize: '0.75rem', color: 'var(--gray-3)', margin: 0 }}>
                ⚠ Saving will recalculate this client's current balance.
              </p>
              <button className={styles.loadConfirmBtn} disabled={txLoading} onClick={handleSaveTx}>
                {txLoading ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </Overlay>
      )}

      {/* Delete Transaction Confirm */}
      {confirmDeleteTx && (
        <Overlay className={styles.overlay} onClose={() => setConfirmDeleteTx(null)}>
          <div className={styles.modal} onClick={e => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3 className={styles.modalTitle}>Delete Transaction</h3>
              <button className={styles.modalClose} onClick={() => setConfirmDeleteTx(null)}>✕</button>
            </div>
            <div className={styles.modalBody}>
              <p style={{ fontSize: '0.9rem', color: 'var(--gray-1)', margin: 0 }}>
                Are you sure you want to delete this transaction? The client's balance will be recalculated automatically.
              </p>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button
                  className={styles.loadConfirmBtn}
                  style={{ background: 'var(--surface-3)', color: 'var(--gray-1)', border: '1px solid var(--border)', flex: 1 }}
                  onClick={() => setConfirmDeleteTx(null)}
                >
                  Cancel
                </button>
                <button
                  className={styles.loadConfirmBtn}
                  style={{ background: 'var(--red)', flex: 1 }}
                  disabled={txLoading}
                  onClick={() => handleDeleteTx(confirmDeleteTx)}
                >
                  {txLoading ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        </Overlay>
      )}

      {/* Confirm Delete Client */}
      {confirmDeleteClient && selected && (
        <Overlay className={styles.overlay} onClose={() => setConfirmDeleteClient(false)}>
          <div className={styles.modal} onClick={e => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3 className={styles.modalTitle}>Remove Client</h3>
              <button className={styles.modalClose} onClick={() => setConfirmDeleteClient(false)}>✕</button>
            </div>
            <div className={styles.modalBody}>
              <p style={{ fontSize: '0.9rem', color: 'var(--gray-1)', margin: 0 }}>
                Are you sure you want to remove <strong>{selected.name}</strong>? Their transaction history will be preserved. You can restore them at any time from the client list.
              </p>
              <div style={{ display: 'flex', gap: 10 }}>
                <button className={styles.loadConfirmBtn}
                  style={{ background: 'var(--surface-3)', color: 'var(--gray-1)', border: '1px solid var(--border)', flex: 1 }}
                  onClick={() => setConfirmDeleteClient(false)}>Cancel</button>
                <button className={styles.loadConfirmBtn}
                  style={{ background: 'var(--red)', flex: 1 }}
                  onClick={handleDeleteClient}>Remove Client</button>
              </div>
            </div>
          </div>
        </Overlay>
      )}

      {/* Edit Individual Athlete Modal */}
      {(editingAthleteId || addingAthlete) && editAthleteData && (
        <Overlay className={styles.overlay} onClose={() => { setEditingAthleteId(null); setAddingAthlete(false); }}>
          <div className={styles.modal} onClick={e => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3 className={styles.modalTitle}>{addingAthlete ? 'Add Athlete' : 'Edit Athlete'}</h3>
              <button className={styles.modalClose} onClick={() => { setEditingAthleteId(null); setAddingAthlete(false); }}>✕</button>
            </div>
            <div className={styles.modalBody}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <div className={styles.formField}>
                  <label className={styles.formLabel}>First Name</label>
                  <input className={styles.formInput}
                    value={editAthleteData.first_name ?? ''}
                    onChange={e => setEditAthleteData(p => ({ ...p, first_name: e.target.value }))} />
                </div>
                <div className={styles.formField}>
                  <label className={styles.formLabel}>Last Name</label>
                  <input className={styles.formInput}
                    value={editAthleteData.last_name ?? ''}
                    onChange={e => setEditAthleteData(p => ({ ...p, last_name: e.target.value }))} />
                </div>
                <div className={styles.formField}>
                  <label className={styles.formLabel}>Position / Sport</label>
                  <input className={styles.formInput} placeholder="e.g. QB, WR"
                    value={editAthleteData.position || ''}
                    onChange={e => setEditAthleteData(p => ({ ...p, position: e.target.value }))} />
                </div>
                <div className={styles.formField}>
                  <label className={styles.formLabel}>Birthday</label>
                  <input type="date" className={styles.formInput}
                    value={editAthleteData.birthday || ''}
                    onChange={e => setEditAthleteData(p => ({ ...p, birthday: e.target.value }))} />
                </div>
                <div className={styles.formField}>
                  <label className={styles.formLabel}>Height</label>
                  <input className={styles.formInput} placeholder="e.g. 5ft 9in"
                    value={editAthleteData.height || ''}
                    onChange={e => setEditAthleteData(p => ({ ...p, height: e.target.value }))} />
                </div>
                <div className={styles.formField}>
                  <label className={styles.formLabel}>Weight (lbs)</label>
                  <input className={styles.formInput} placeholder="e.g. 145"
                    value={editAthleteData.weight || ''}
                    onChange={e => setEditAthleteData(p => ({ ...p, weight: e.target.value }))} />
                </div>
                <div className={styles.formField}>
                  <label className={styles.formLabel}>Team / School</label>
                  <input className={styles.formInput} placeholder="e.g. Parker Youth Football"
                    value={editAthleteData.team || ''}
                    onChange={e => setEditAthleteData(p => ({ ...p, team: e.target.value }))} />
                </div>
                <div className={styles.formField}>
                  <label className={styles.formLabel}>Graduating Class</label>
                  <input className={styles.formInput} placeholder="e.g. 2030"
                    value={editAthleteData.grad_year || ''}
                    onChange={e => setEditAthleteData(p => ({ ...p, grad_year: e.target.value }))} />
                </div>
              </div>
              <button className={styles.loadConfirmBtn} disabled={athleteLoading} onClick={handleSaveAthlete}>
                {athleteLoading ? 'Saving...' : addingAthlete ? 'Add Athlete' : 'Save Athlete'}
              </button>
            </div>
          </div>
        </Overlay>
      )}

      {/* Confirm Delete Assessment */}
      {confirmDeleteAssessment && (
        <Overlay className={styles.overlay} onClose={() => setConfirmDeleteAssessment(null)}>
          <div className={styles.modal} onClick={e => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3 className={styles.modalTitle}>Delete Assessment</h3>
              <button className={styles.modalClose} onClick={() => setConfirmDeleteAssessment(null)}>✕</button>
            </div>
            <div className={styles.modalBody}>
              <p style={{ fontSize: '0.9rem', color: 'var(--gray-1)', margin: 0 }}>
                Are you sure you want to delete this assessment? All scores, strengths, weaknesses, and the summary will be permanently removed.
              </p>
              <div style={{ display: 'flex', gap: 10 }}>
                <button className={styles.loadConfirmBtn}
                  style={{ background: 'var(--surface-3)', color: 'var(--gray-1)', border: '1px solid var(--border)', flex: 1 }}
                  onClick={() => setConfirmDeleteAssessment(null)}>Cancel</button>
                <button className={styles.loadConfirmBtn}
                  style={{ background: 'var(--red)', flex: 1 }}
                  onClick={() => { handleDeleteAssessment(confirmDeleteAssessment); setConfirmDeleteAssessment(null); }}>
                  Delete Assessment
                </button>
              </div>
            </div>
          </div>
        </Overlay>
      )}

      {/* Add / Edit Assessment Modal */}
      {(addingAssessment || editingAssessment) && (
        <Overlay className={styles.overlay} onClose={() => { setAddingAssessment(null); setEditingAssessment(null); }}>
          <div className={styles.modal} style={{ width: 560, maxWidth: 'calc(100vw - 32px)' }} onClick={e => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3 className={styles.modalTitle}>{editingAssessment ? 'Edit Assessment' : 'Add Assessment'}</h3>
              <button className={styles.modalClose} onClick={() => { setAddingAssessment(null); setEditingAssessment(null); }}>✕</button>
            </div>
            <div className={styles.modalBody}>

              {/* Date */}
              <div className={styles.formField}>
                <label className={styles.formLabel}>Assessment Date</label>
                <input type="date" className={styles.formInput}
                  value={assessmentForm.assessment_date}
                  onChange={e => setAssessmentForm(p => ({ ...p, assessment_date: e.target.value }))} />
              </div>

              {/* Assessment Type */}
              <div className={styles.formField}>
                <label className={styles.formLabel}>Assessment Type</label>
                <select className={styles.formInput}
                  value={assessmentForm.assessment_type}
                  onChange={e => setAssessmentForm(p => ({ ...p, assessment_type: e.target.value }))}>
                  <option value="evaluation">Evaluation</option>
                  <option value="test_day">Test Day</option>
                </select>
              </div>

              {/* Test Scores */}
              {assessmentMetrics.length > 0 && (
                <div className={styles.formField}>
                  <label className={styles.formLabel}>Test Scores</label>
                  <div className={styles.scoresGrid}>
                    {assessmentForm.scores.map((s, i) => (
                      <div key={s.metric_key} className={styles.scoreInputRow}>
                        <span className={styles.scoreInputLabel}>{s.label}</span>
                        <input className={styles.scoreInputField}
                          type="number" step="0.01" placeholder="—"
                          value={s.value}
                          onChange={e => {
                            const scores = assessmentForm.scores.map((sc, idx) =>
                              idx === i ? { ...sc, value: e.target.value } : sc
                            );
                            setAssessmentForm(p => ({ ...p, scores }));
                          }} />
                        <span className={styles.scoreInputUnit}>{s.unit}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Strengths */}
              <div className={styles.formField}>
                <label className={styles.formLabel}>Strengths</label>
                {assessmentForm.strengths.map((s, i) => (
                  <div key={i} className={styles.bulletRow}>
                    <input className={styles.formInput} placeholder="e.g. Route running — quick and explosive"
                      value={s}
                      onChange={e => {
                        const strengths = assessmentForm.strengths.map((x, idx) => idx === i ? e.target.value : x);
                        setAssessmentForm(p => ({ ...p, strengths }));
                      }} />
                    {assessmentForm.strengths.length > 1 && (
                      <button className={styles.removeBulletBtn}
                        onClick={() => setAssessmentForm(p => ({ ...p, strengths: p.strengths.filter((_, idx) => idx !== i) }))}>✕</button>
                    )}
                  </div>
                ))}
                <button className={styles.addBulletBtn}
                  onClick={() => setAssessmentForm(p => ({ ...p, strengths: [...p.strengths, ''] }))}>
                  + Add Strength
                </button>
              </div>

              {/* Weaknesses */}
              <div className={styles.formField}>
                <label className={styles.formLabel}>Areas to Improve</label>
                {assessmentForm.weaknesses.map((w, i) => (
                  <div key={i} className={styles.bulletRow}>
                    <input className={styles.formInput} placeholder="e.g. Top end speed (10–20 yds)"
                      value={w}
                      onChange={e => {
                        const weaknesses = assessmentForm.weaknesses.map((x, idx) => idx === i ? e.target.value : x);
                        setAssessmentForm(p => ({ ...p, weaknesses }));
                      }} />
                    {assessmentForm.weaknesses.length > 1 && (
                      <button className={styles.removeBulletBtn}
                        onClick={() => setAssessmentForm(p => ({ ...p, weaknesses: p.weaknesses.filter((_, idx) => idx !== i) }))}>✕</button>
                    )}
                  </div>
                ))}
                <button className={styles.addBulletBtn}
                  onClick={() => setAssessmentForm(p => ({ ...p, weaknesses: [...p.weaknesses, ''] }))}>
                  + Add Area
                </button>
              </div>

              {/* Summary */}
              <div className={styles.formField}>
                <label className={styles.formLabel}>Coach Summary</label>
                <textarea className={styles.formInput} rows={4}
                  placeholder="Write a narrative summary of the athlete's progress and potential..."
                  value={assessmentForm.summary}
                  onChange={e => setAssessmentForm(p => ({ ...p, summary: e.target.value }))}
                  style={{ resize: 'vertical', minHeight: 80 }} />
              </div>

              <button className={styles.loadConfirmBtn} onClick={handleSaveAssessment}>
                {editingAssessment ? 'Save Changes' : 'Save Assessment'}
              </button>
            </div>
          </div>
        </Overlay>
      )}

      {/* Statement Preview Modal */}
      {showStatementPreview && statementTarget && (
        <Overlay className={styles.overlay} onClose={() => setShowStatementPreview(false)}>
          <div className={styles.modal} onClick={e => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3 className={styles.modalTitle}>Statement Preview</h3>
              <button className={styles.modalClose} onClick={() => setShowStatementPreview(false)}>✕</button>
            </div>
            <div className={styles.modalBody}>
              <p style={{ fontSize: '0.82rem', color: 'var(--gray-2)', margin: 0 }}>
                This statement will be emailed to <strong style={{ color: 'var(--white)' }}>{statementTarget.email}</strong>
              </p>

              {/* Balance */}
              <div style={{ background: 'var(--surface-3)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '16px 20px' }}>
                <div style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '2px', textTransform: 'uppercase', color: '#888', marginBottom: 6 }}>Current Account Balance</div>
                <div style={{ fontSize: '28px', fontWeight: 900, color: parseFloat(statementTarget.balance) <= 0 ? 'var(--red)' : parseFloat(statementTarget.balance) < 75 ? 'var(--yellow)' : 'var(--green)', fontFamily: 'var(--font-display)' }}>
                  ${Number(statementTarget.balance).toFixed(2)}
                </div>
              </div>

              {/* Recent transactions preview */}
              {clientTx.length > 0 && (
                <>
                  <div style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '2px', textTransform: 'uppercase', color: '#888', marginTop: 4 }}>Recent Activity (last {Math.min(5, clientTx.length)})</div>
                  <div className={styles.statementPreview}>
                    {clientTx.slice(0, 5).map(tx => (
                      <div key={tx.id} className={styles.statementPreviewRow}>
                        <span className={styles.statementPreviewLabel}>
                          {fmtDate(tx.sessionDate || tx.createdAt)} — {tx.type === 'credit_load' ? 'Credit Loaded' : tx.note || tx.sessionType}
                        </span>
                        <span className={tx.amount > 0 ? styles.statementPreviewCredit : styles.statementPreviewDebit}>
                          {tx.amount > 0 ? '+' : '-'}${Math.abs(Number(tx.amount)).toFixed(2)}
                        </span>
                      </div>
                    ))}
                    {(() => {
                      const last5 = clientTx.slice(0, 5);
                      const net = last5.reduce((s, t) => s + Number(t.amount), 0);
                      return (
                        <div className={`${styles.statementPreviewRow} ${styles.statementPreviewTotal}`}>
                          <span className={styles.statementPreviewLabel}>Activity Total</span>
                          <span className={net >= 0 ? styles.statementPreviewCredit : styles.statementPreviewDebit}>
                            {net >= 0 ? '+' : '-'}${Math.abs(net).toFixed(2)}
                          </span>
                        </div>
                      );
                    })()}
                  </div>
                </>
              )}

              <button
                className={styles.statementSendBtn}
                disabled={statementSending[statementTarget.id]}
                onClick={() => {
                  setShowStatementPreview(false);
                  handleSendStatement(statementTarget);
                }}
              >
                {statementSending[statementTarget.id] ? 'Sending...' : '✉ Send Statement to Client'}
              </button>
            </div>
          </div>
        </Overlay>
      )}

      {/* Add Client Modal */}
      {showAdd && (
        <Overlay className={styles.overlay} onClose={() => setShowAdd(false)}>
          <div className={styles.modal} onClick={e => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3 className={styles.modalTitle}>Add New Client</h3>
              <button className={styles.modalClose} onClick={() => setShowAdd(false)}>✕</button>
            </div>
            <div className={styles.modalBody}>
              <div className={styles.editSectionTitle}>Parent / Guardian</div>
              <div className={styles.formField}>
                <label className={styles.formLabel}>Parent Name *</label>
                <input className={styles.formInput} placeholder="e.g. Sarah Johnson" value={newClient.name} onChange={e => setNewClient(p => ({ ...p, name: e.target.value }))} />
              </div>
              <div className={styles.formField}>
                <label className={styles.formLabel}>Email *</label>
                <input type="email" className={styles.formInput} placeholder="parent@email.com" value={newClient.email} onChange={e => setNewClient(p => ({ ...p, email: e.target.value }))} />
              </div>
              <div className={styles.formField}>
                <label className={styles.formLabel}>Phone</label>
                <input type="tel" className={styles.formInput} placeholder="(720) 555-0000" value={newClient.phone} onChange={e => setNewClient(p => ({ ...p, phone: formatPhone(e.target.value) }))} />
              </div>
              <AthleteFields athletes={newClient.athletes} onChange={athletes => setNewClient(p => ({ ...p, athletes }))} />
              <EmergencyFields data={newClient} parentName={newClient.name} parentPhone={newClient.phone} onChange={updated => setNewClient(p => ({ ...p, ...updated }))} />
              <button className={styles.loadConfirmBtn} disabled={loading} onClick={handleAddClient}>
                {loading ? 'Adding...' : 'Add Client'}
              </button>
            </div>
          </div>
        </Overlay>
      )}

      {showBookModal && selected && (
        <BookForClient
          API={API}
          authHeader={authHeader}
          showToast={showToast}
          client={selected}
          onClose={() => setShowBookModal(false)}
          onBooked={() => refresh && refresh()}
        />
      )}
    </div>
  );
}
